import {Color, HorizontalOrigin, VerticalOrigin, HeightReference, Cartesian2, PolylineGlowMaterialProperty, PolylineDashMaterialProperty} from 'cesium';
import MapIcon from '@vtx/map-icon/lib/map-icon';
import isObject from '../_util/isObject';
import transformRgb from '../_util/transformRgb';
import isFunction from '../_util/isFunction';
import StyleCache from '../_util/StyleCache';
import {isString} from 'lodash';

const Cache = window.__styleCache || new StyleCache();
window.__styleCache = Cache;

/***
 * 获取图标地址
 * @param type MapIcon 的图标绘制类型
 * @param options 图标对应的参数
 * @returns {*}
 */
export const getImgUrl = (type, options = {}) => {
    // 根据参数生成key
    const key = `${type}-${JSON.stringify(options)}`;

    // 判断是否有缓存
    if (Cache.has(key)) {
        // 直接从缓存中获取
        return Cache.get(key);
    }

    // 生成图标
    const imgUrl = new MapIcon[type](options).getImgUrl();

    // 存入缓存
    Cache.set(key, imgUrl);

    return imgUrl;
}

export const getImage = options => {
    let image;
    if (typeof options === 'string') {
        image = options;
    } else if (isObject(options)) {
        let {
            type = 'CommonIcon2',
            icon,
            image: _image,
            color = '#1890ff',
            size,
            text,
            ...other
        } = options;
        // 图标类型 CockpitIcon CockpitIcon2 CommonIcon CommonIcon2
        if (type === 'PointIcon') {
            image = getImgUrl(type, {size, color, ...other});
        } else if (MapIcon[type] && text) {
            image = getImgUrl(type, {size, color, text, ...other});
        } else if (MapIcon[type] && MapIcon.icon[icon]) {
            image = getImgUrl(type, {canvasIcon: icon, color, size, ...other});
        } else {
            image = _image;
        }
    }

    return image;
};

export const getColor = style => {
    if (isObject(style)) {
        let { color, glowPower, opacity, lineDash } = style;

        let _color = Color.fromCssColorString(transformRgb(color, opacity));
        if (glowPower) {
            return new PolylineGlowMaterialProperty({
                glowPower: glowPower, // 发光强度
                color: _color,
            })
        } else if (lineDash) {
            return new PolylineDashMaterialProperty({
                color: _color,
                dashLength: lineDash //短划线长度
            })
        }

        return _color;
    }

    if (typeof style === 'string') {
        return Color.fromCssColorString(style);
    }
};

/**
 *
 * @param {*} options
 * pixelOffset: 偏移量
 * @returns
 */
export const Label = options => {
    const {text, align, justify, size, color, background, fontFamily, offset, pixelOffset, ...v} = options;

    let style = {
        text,
        fillColor: getColor(color),
        font: `${size || 16}px ${fontFamily || 'Helvetica'}`, //先字号再字体
        showBackground: !!background, //是否显示背景色,就是文字后面的阴影
        backgroundColor: getColor(background),

        heightReference: HeightReference.CLAMP_TO_GROUND,
        ...v
    };

    // 垂直
    style.verticalOrigin = {
        "top": VerticalOrigin.BOTTOM, // 原点在对象的底部，所以标签往上展示
        "middle": VerticalOrigin.CENTER, // 原点位于 BASELINE 和 TOP 之间的垂直中心
        "bottom": VerticalOrigin.TOP, // 原点在对象的顶部,所以标签往下展示
        "baseline": VerticalOrigin.BASELINE, // 如果对象包含文本，则原点位于文本的基线，否则原点位于对象的底部
    }[align || 'bottom'];

    // 水平
    style.horizontalOrigin = {
        "right": HorizontalOrigin.LEFT, // 原点在对象的左侧
        "center": HorizontalOrigin.CENTER, // 原点在对象的水平中心
        "left": HorizontalOrigin.RIGHT, // 原点在对象的右侧
    }[justify || 'center']

    if (offset) {
        if (Array.isArray(offset)) {
            style.pixelOffset = new Cartesian2(Number(offset[0]), Number(offset[1]))
        } else {
            style.pixelOffset = new Cartesian2(Number(offset), 0)
        }
    }
    if (typeof pixelOffset?.x == 'number' && typeof pixelOffset?.y == 'number') {
        style.pixelOffset = new Cartesian2(pixelOffset?.x, pixelOffset?.y);
    }

    return style;
};

/**
 *
 * @param {*} options
 * pixelOffset: 偏移量
 * @returns
 */
export const Point = options => {
    const {radius, fill, stroke, pixelOffset = {x: 0, y: 0}} = options;

    let style = {
        pixelSize: radius, // 以像素为单位指定大小
    };

    if (fill) {
        style = {
            ...style,
            color: getColor(fill),
        };
    }

    if (stroke) {
        style = {
            ...style,
            outline: true,
            outlineColor: getColor(stroke),
            outlineWidth: stroke.width || 2,
        };
    }
    if (typeof pixelOffset?.x == 'number' && typeof pixelOffset?.y == 'number') {
        style.pixelOffset = new Cartesian2(pixelOffset?.x, pixelOffset?.y);
    }
    if (options?.ellipse) {
        style.ellipse = options.ellipse;
    }

    return style;
};

export const Polyline = options => {
    let style = {
        width: 2,
    };
    if (options?.stroke) {
        const { stroke } = options;
        style = {
            ...style,
            width: stroke.width || 2,
            material: getColor(stroke),
        };
    }
    if (options?.material) {
        style.material = options.material;
    }
    return style;
};

export const Polygon = options => {
    let style = { ...options };

    if (options) {
        const { fill, stroke, material } = options;

        if (fill) {
            style = {
                ...style,
                fill: true,
                material: material || getColor(fill),
            };
        }

        if (stroke) {
            style = {
                ...style,
                outline: true,
                outlineColor: getColor(stroke),
                outlineWidth: stroke.width || 2,
            };
        }
    }

    return style;
};

export const Marker = options => {
    const { type, icon, image, imageColor, color, size, mapIcon, ...otherOptions } = options || {};
    let style = {
        horizontalOrigin: HorizontalOrigin.CENTER,
        verticalOrigin: VerticalOrigin.BOTTOM,
        heightReference: HeightReference.CLAMP_TO_GROUND,
        clampToGround: true,
        mapIcon: { ...options },
        ...otherOptions,
    };

    if (imageColor) {
        style.color = getColor(imageColor)
    }

    if (options?.mapIcon) {
        style.image = getImage(options.mapIcon);
        let mapIcon = new MapIcon[type || 'CommonIcon2'](options.mapIcon);
        style.width = style.width || mapIcon?.width || 44;
        style.height = style.height || mapIcon?.height || (options.mapIcon.hideFooter ? 44 : 60);
    } else if (options) {
        const {pixelOffset, ellipse, ...v} = options;
        style.image = getImage(v);
    }

    if (options?.material) {
        style.material = options.material;
    }

    return style;
};

const TYPE = {
    Label: Label,
    Point: Point,
    Polyline: Polyline,
    Polygon: Polygon,
    Circle: Point,
    Marker: Marker,
};

export const Renderer = (type, style = {}) => {
    if (!type) {
        new Error('缺少类型');
        return;
    }

    if (!TYPE[type]) {
        new Error('没有这个类型类型的样式');
        return;
    }
    return TYPE[type](style);
};

// 简单渲染
export const Simple = ({ type, style }) => {
    return Renderer(type, style);
};

// 唯一值渲染
export const UniqueValue = (feature, { field, render = [] }) => {
    let value = feature.properties.properties._value[field];
    let renderer = render.find(v => String(v.value) === String(value));
    if (renderer) {
        return Simple(renderer);
    }
};

// 区间渲染
export const ClassBreaks = (feature, { field, render = [] }) => {
    let value = feature.properties.properties._value[field];

    let renderer = render.find(
        v => Number(value) >= Number(v.value[0]) && Number(value) <= Number(v.value[1]),
    );

    if (renderer) {
        return Simple(renderer);
    }
};

// 设置图形对象样式
export const setEntityStyle = (entity, style) => {
    for (let i in style) {
        if (entity.hasOwnProperty(i)) {
            if (i === 'material') {
                entity[i] = style[i];
            } else if (entity[i].setValue) {
                entity[i].setValue(style[i]);
            } else {
                entity[i] = style[i];
            }
        } else {
            entity[i] = style[i];
        }
    }
};

// 设置渲染方式
export const setRenderer = (entity, style) => {
    if (style?.style?.ellipse) {
        entity.ellipse = style?.style?.ellipse;
    }
    if (isFunction(style)) {
        let _style = style(entity);
        entity.setStyle(Simple(_style));
    } else if (isObject(style)) {
        let { mode } = style;
        if (mode) {
            if (mode === 'Simple') {
                entity.setStyle(Simple(style));
            }

            if (mode === 'UniqueValue') {
                entity.setStyle(UniqueValue(entity, style));
            }

            if (mode === 'ClassBreaks') {
                entity.setStyle(ClassBreaks(entity, style));
            }
        } else if (style.style) {
            entity.setStyle(style.style);
        } else {
            entity.setStyle(Simple({ ...style }));
        }
    } else if (isString(style)) {
        // 如果为字符串，判断为内部图标
        let image = getImage({ icon: style });
        entity.setStyle({ image });
    }
};
